import type { Agent } from '@studio/models/agent';

/**
 * embedded link settle: bounded read-after-write poll for the just-linked agent.
 *
 * `handleLink` (auth.tsx) used to force-refresh the room's agent enumeration
 * exactly ONCE after `linkAgentWithAuth` and navigate. When the registry had
 * not yet propagated the just-linked agent (read-after-write lag), that single
 * fetch RE-CACHED the stale pre-link agent set for ROOM_CACHE_TTL (60s,
 * api/agent/registry.ts) — and `useRoomAgentStatus` never re-enumerates on its
 * own (`retry: false`, no refetchInterval, staleTime 10s). The `/room/:id/boot`
 * route then dead-spotted on the stale set (zero agents on a relink to an empty
 * studio; the just-linked camera missing next to an offline/online bridge) and
 * only a manual refresh recovered — the "refresh is still required to load the
 * targeted studio" embedded-link regression family.
 *
 * Electron's auto-link flow closes the same race with a settle poll
 * (`awaitAutoLinkSettle`, routes/root/root-room-sync.ts); this is the web link
 * flow's equivalent: keep force-fetching (bypassing the room cache) until the
 * linked agent actually appears, so the enumeration the boot route reads is
 * post-link. Pure orchestration with an injected fetch for unit-testability,
 * mirroring root-room-sync.ts / auth/room-selection.ts.
 * Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).
 */

// ~5s of polling on top of per-attempt fetch time. Registry propagation is
// normally sub-second; the cap keeps the wizard's "Validating Camera…" busy
// state bounded. On exhaustion the caller still navigates — the boot-stall
// watchdog (routes/boot-overlay/boot-stall.ts) is the terminal backstop.
export const AWAIT_LINKED_AGENT_ATTEMPTS = 10;
export const AWAIT_LINKED_AGENT_DELAY_MS = 500;

/**
 * Poll the room's agent enumeration until it contains `agentId`.
 *
 * @param agentId - The just-linked agent expected to appear.
 * @param fetchAgents - Force-fetch of the room's agent set keyed by agent id.
 *   MUST bypass caches (pass `force: true` to `fetchAgentsForRoom`) — a cached
 *   read would just re-observe the stale snapshot this poll exists to outwait.
 * @param attempts - Probe ceiling.
 * @param delayMs - Pause between probes.
 * @returns Whether the agent appeared before the attempts were exhausted.
 */
export const awaitLinkedAgentInRoom = async ({
  agentId,
  fetchAgents,
  attempts = AWAIT_LINKED_AGENT_ATTEMPTS,
  delayMs = AWAIT_LINKED_AGENT_DELAY_MS
}: {
  readonly agentId: Agent['id'];
  readonly fetchAgents: () => Promise<Record<string, unknown>>;
  readonly attempts?: number;
  readonly delayMs?: number;
}): Promise<boolean> => {
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const agents = await fetchAgents();

      if (agentId in agents) {
        return true;
      }
    } catch {
      // A failed probe is a missed attempt, not a flow error — the link
      // mutation itself already succeeded; keep polling until the ceiling.
    }

    // No pause after the final attempt — the caller proceeds immediately.
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return false;
};
